/**
 * Field block (design README "Field block"): label → control → helper/error, one pattern per
 * field. Wires `aria-describedby` (helper + error) and returns the ids the control must spread so
 * error text is announced and `aria-invalid` is set (WCAG 3.3.1). Renders children via a
 * render-prop so any control (input/textarea/group) reuses the same a11y plumbing.
 */
import type { ReactNode } from "react";
import { AlertIcon } from "./icons.js";

export interface FieldAria {
  id: string;
  "aria-describedby"?: string;
  "aria-invalid"?: true;
  "aria-required"?: true;
  /**
   * The label's own id, for controls where a native `<label for>` association is unreliable
   * (e.g. a non-form-element `role="textbox"` like RichTextEditor's contentEditable div — `for`
   * targets native form controls per the HTML spec; ARIA widgets need `aria-labelledby`).
   * Native inputs/selects/textareas can ignore this and keep relying on `htmlFor`.
   */
  "aria-labelledby": string;
}

interface FieldProps {
  id: string;
  label: ReactNode;
  helper?: ReactNode;
  error?: string;
  required?: boolean;
  /** Optional visual "(optional)" tag when not required. */
  optionalHint?: boolean;
  children: (aria: FieldAria) => ReactNode;
}

export function Field({
  id,
  label,
  helper,
  error,
  required,
  optionalHint,
  children,
}: FieldProps): JSX.Element {
  const helperId = helper ? `${id}-helper` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(" ") || undefined;
  const labelId = `${id}-label`;

  const aria: FieldAria = {
    id,
    "aria-describedby": describedBy,
    "aria-labelledby": labelId,
    ...(error ? { "aria-invalid": true } : {}),
    ...(required ? { "aria-required": true } : {}),
  };

  return (
    <div className="td-field">
      <label className="td-field__label" id={labelId} htmlFor={id}>
        {label}
        {required ? (
          <span className="td-field__req" aria-hidden="true">
            {" "}
            *
          </span>
        ) : optionalHint ? (
          <span className="td-field__optional"> (optional)</span>
        ) : null}
      </label>
      {helper ? (
        <p className="td-field__helper" id={helperId}>
          {helper}
        </p>
      ) : null}
      {children(aria)}
      {error ? (
        <p className="td-field__error" id={errorId}>
          <AlertIcon width={16} height={16} />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}
